import { faArrowLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Logo from 'components/common/Logo';
import Section from 'components/common/Section';
import ViewEntryDialog from 'components/modals/ViewEntryDialog';
import React, { useRef, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { showAlert } from 'stores/modal';
import api from 'util/api';
import logger from 'util/logger';

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isNotFoundError = err => {
  const status =
    err?.StatusCode ||
    err?.status ||
    err?.response?.status ||
    err?.response?.statusCode;

  return status === 404 || err?.Code === 'NotFound' || err?.code === 'NotFound';
};

const ViewSigning = () => {
  const dispatch = useDispatch();
  const [signingGuid, setSigningGuid] = useState('');
  const [loadedSigning, setLoadedSigning] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const normalizedGuid = signingGuid.trim();

  const showValidationAlert = () => {
    dispatch(
      showAlert(
        'Invalid Signing GUID',
        'Enter a valid signing GUID and try again.'
      )
    );
  };

  const handleSubmit = evt => {
    evt.preventDefault();

    if (!GUID_PATTERN.test(normalizedGuid)) {
      showValidationAlert();
      return;
    }

    setLoading(true);
    api.SigningPublic.get(normalizedGuid)
      .then(res => {
        setLoadedSigning(res);
      })
      .catch(err => {
        logger.log('error loading public signing', err);
        if (isNotFoundError(err)) {
          dispatch(
            showAlert(
              <strong>Signing Not Found</strong>,
              "We're sorry, but we could not find a signing with the identifier you specified."
            )
          );
        } else {
          dispatch(
            showAlert(
              'Unable to View Signing',
              'There was a problem loading the signing. Please try again.'
            )
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBack = () => {
    setLoadedSigning(null);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (loadedSigning) {
    return (
      <Section className="py-4 py-lg-5" fluid>
        <div className="mx-auto" style={{ maxWidth: '1200px' }}>
          <Button
            variant="outline-primary"
            className="mb-3"
            onClick={handleBack}
          >
            <FontAwesomeIcon icon={faArrowLeft} size="sm" /> Back
          </Button>
          <ViewEntryDialog
            key={loadedSigning.GUID}
            entry={loadedSigning}
            readOnly
            publicMode
            inline
          />
        </div>
      </Section>
    );
  }

  return (
    <Section className="py-0">
      <Row className="flex-center min-vh-100 py-6">
        <Col sm={10} md={8} lg={6} xl={5} className="col-xxl-4">
          <Logo width={40} />
          <Card>
            <Card.Body className="p-4 p-sm-5">
              <Form onSubmit={handleSubmit}>
                <p className="mb-3">
                  Please enter the unique identifier of the signing you wish to
                  view, and click "View Signing".
                </p>
                <Form.Group className="mb-3" controlId="publicSigningGuid">
                  <Form.Label>Signing GUID</Form.Label>
                  <Form.Control
                    ref={inputRef}
                    type="text"
                    value={signingGuid}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    disabled={loading}
                    onChange={evt => setSigningGuid(evt.target.value)}
                    autoComplete="off"
                  />
                </Form.Group>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || normalizedGuid.length === 0}
                  className="w-100"
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} size="sm" />
                  )}{' '}
                  View Signing
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Section>
  );
};

export default ViewSigning;
